import { KonvaEventObject } from "konva/types/Node";
import * as React from "react";

export type LineTool = "Pen" | "Eraser";
export type LineConfig = { points: number[]; tool: LineTool; strokeWidth: number; strokeColor: string };
interface UseImageDrawingOptions {
	fileId: string;
	roomId: string;
	strokeColor?: string;
	scale?: number;
	socket: SocketIOClient.Socket;
}

export function useImageDrawing({ scale = 1, fileId, socket, roomId, strokeColor = "#000" }: UseImageDrawingOptions) {
	const [tool, setTool] = React.useState<LineTool>("Pen");
	const [strokeWidth, setStrokeWidth] = React.useState<number>(3);
	const [lines, setLines] = React.useState<LineConfig[]>([]);
	const [currentLine, setCurrentLine] = React.useState<LineConfig>();
	const isDrawing = React.useRef(false);

	const handleMouseDown = (e: KonvaEventObject<MouseEvent>) => {
		isDrawing.current = true;
		const pos = e.currentTarget.getStage()!.getPointerPosition()!;

		// Init currentLine data
		setCurrentLine({ points: [pos.x / scale, pos.y / scale], tool, strokeWidth, strokeColor });
	};

	const handleMouseMove = (e: KonvaEventObject<MouseEvent>) => {
		// no drawing - skipping
		if (!isDrawing.current || !currentLine) {
			return;
		}
		const stage = e.target.getStage()!;
		const point = stage.getPointerPosition()!;

		// Append points to current line
		setCurrentLine((prevLine) => {
			return {
				...prevLine!,
				points: [...prevLine!.points, point.x / scale, point.y / scale],
			};
		});
	};

	const handleMouseUp = (e: KonvaEventObject<MouseEvent>) => {
		e.target.getStage();
		isDrawing.current = false;

		if (!currentLine) return;

		// Add drawed line to lines
		setLines((prev) => [...prev, currentLine]);
		// Send line to server
		socket.emit("onImageDrawEnd", {
			roomId,
			fileId,
			drawData: currentLine,
		});
		// Reset line
		setCurrentLine(undefined);
	};

	const clearLines = () => {
		setLines([]);
		// Send message to clear lines
		socket.emit("onClearDrawData", { roomId, fileId });
	};

	const changeTool = (tool: LineTool) => {
		setTool(tool);
		if (tool === "Pen") {
			setStrokeWidth(3);
		} else {
			setStrokeWidth(20);
		}
	};

	React.useEffect(() => {
		// Initialize line on mount
		socket.emit("getImageDrawData", { roomId, fileId });
		socket.on("initDrawData", (_roomId, id, drawData) => {
			if (id === fileId && roomId === _roomId) setLines(drawData);
		});
	}, [fileId, roomId, socket]);

	React.useEffect(() => {
		// Append line when other user draw something
		socket.on("drawLine", (_roomId, id, newLine) => {
			if (fileId === id && roomId === _roomId) setLines((prev) => [...prev, newLine]);
		});
	}, [fileId, roomId, socket]);

	React.useEffect(() => {
		// Clear lines
		socket.on("clearDrawData", (_roomId, id) => {
			if (id === fileId && roomId === _roomId) setLines([]);
		});
	}, [fileId, roomId, socket]);

	return {
		currentLine,
		lines,
		tool,
		strokeWidth,
		strokeColor,
		handleMouseDown,
		handleMouseUp,
		handleMouseMove,
		clearLines,
		changeTool,
	};
}
